import { z } from 'zod';

/**
 * The custom fields contract (docs/specs/004-clientes-e-contatos/spec.md, rules 25–33): the
 * definitions an organization adds to customers, contacts and deals, and the values records keep
 * under `customFields` keyed by the definition's `key`. Types and zod schemas are the API's
 * shape; the forms and the mocked API both validate with them.
 */

/** `case` (spec 046): the service case, whose values live on the case itself. */
export const CUSTOM_FIELD_ENTITIES = ['customer', 'contact', 'deal', 'lead', 'case'] as const;
export type CustomFieldEntity = (typeof CUSTOM_FIELD_ENTITIES)[number];

export const CUSTOM_FIELD_TYPES = [
  'text',
  'long_text',
  'number',
  'money',
  'date',
  'boolean',
  'single_select',
  'multi_select',
  'email',
  'phone',
  'url',
] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

export const SELECT_FIELD_TYPES: readonly CustomFieldType[] = ['single_select', 'multi_select'];

export function isSelectFieldType(type: CustomFieldType): boolean {
  return SELECT_FIELD_TYPES.includes(type);
}

/** Types a list can filter by "contains". */
export const TEXT_FILTER_FIELD_TYPES: readonly CustomFieldType[] = [
  'text',
  'long_text',
  'email',
  'phone',
  'url',
];

export const CUSTOM_FIELD_SOURCES = ['user', 'plugin'] as const;
export type CustomFieldSource = (typeof CUSTOM_FIELD_SOURCES)[number];

export const CUSTOM_FIELD_LIMITS = {
  labelMin: 2,
  labelMax: 60,
  groupMax: 60,
  optionsMin: 1,
  optionsMax: 50,
  optionLabelMax: 60,
  textMax: 255,
  longTextMax: 5000,
} as const;

export interface CustomFieldOption {
  id: string;
  label: string;
}

export interface CustomFieldDefinition {
  id: string;
  entity: CustomFieldEntity;
  /** `snake_case`, generated from the label on creation; never changes. */
  key: string;
  label: string;
  type: CustomFieldType;
  required: boolean;
  /** Select types only; empty for the others. */
  options: CustomFieldOption[];
  /** Section of the form ("Dados comerciais"); `null` = the default section. */
  group: string | null;
  /** Deal fields: the pipelines where the field shows; empty = every pipeline (QA-24). */
  pipelineIds: string[];
  order: number;
  active: boolean;
  source: CustomFieldSource;
  /** The plugin that owns a `plugin` field. */
  pluginId: string | null;
  /** Spec 074, R18: the connected system that owns the value; read-only here. */
  managedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A stored value: text types and dates are strings (`YYYY-MM-DD`; phones are digits), money
 * is integer cents, a single select is an option id, a multi select the option ids.
 */
export type CustomFieldValue = string | number | boolean | string[] | null;
export type CustomFieldValues = Record<string, CustomFieldValue>;

export const customFieldValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.null(),
]);

export const customFieldValuesSchema = z.record(z.string(), customFieldValueSchema);

/* ------------------------------------------------------------------ definitions */

const label = z.string().trim().min(CUSTOM_FIELD_LIMITS.labelMin).max(CUSTOM_FIELD_LIMITS.labelMax);

const optionInput = z.object({
  /** Absent on a new option: the API gives it an id. */
  id: z.string().min(1).max(40).optional(),
  label: z.string().trim().min(1).max(CUSTOM_FIELD_LIMITS.optionLabelMax),
});

const options = z.array(optionInput).max(CUSTOM_FIELD_LIMITS.optionsMax);

const group = z.string().trim().max(CUSTOM_FIELD_LIMITS.groupMax).nullable();

export type CustomFieldOptionInput = z.infer<typeof optionInput>;

export const createCustomFieldSchema = z
  .object({
    entity: z.enum(CUSTOM_FIELD_ENTITIES),
    label,
    type: z.enum(CUSTOM_FIELD_TYPES),
    required: z.boolean().optional(),
    options: options.optional(),
    group: group.optional(),
    active: z.boolean().optional(),
    pipelineIds: z.array(z.string().min(1)).optional(),
  })
  .strict();

export type CreateCustomFieldInput = z.input<typeof createCustomFieldSchema>;

export const updateCustomFieldSchema = z
  .object({
    label: label.optional(),
    required: z.boolean().optional(),
    options: options.optional(),
    group: group.optional(),
    active: z.boolean().optional(),
    pipelineIds: z.array(z.string().min(1)).optional(),
  })
  .strict();

export type UpdateCustomFieldInput = z.input<typeof updateCustomFieldSchema>;

export const reorderCustomFieldsSchema = z
  .object({ entity: z.enum(CUSTOM_FIELD_ENTITIES), ids: z.array(z.string().min(1)).min(1) })
  .strict();

export type ReorderCustomFieldsInput = z.input<typeof reorderCustomFieldsSchema>;

/** Text compared without case and accents (labels, option labels). */
export function normalizeLabel(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .toLowerCase();
}

/**
 * What is wrong with the options of a definition of `type`: select types need 1–50 options
 * with distinct labels; the other types take none. `null` when they are fine.
 */
export function optionsIssue(
  type: CustomFieldType,
  list: readonly { label: string }[] | undefined,
): 'options_required' | 'options_not_allowed' | 'options_duplicated' | null {
  const count = list?.length ?? 0;
  if (!isSelectFieldType(type)) return count > 0 ? 'options_not_allowed' : null;
  if (count < CUSTOM_FIELD_LIMITS.optionsMin) return 'options_required';
  const labels = new Set((list ?? []).map((option) => normalizeLabel(option.label)));
  return labels.size === count ? null : 'options_duplicated';
}

/** `"Potencial de compra"` → `"potencial_de_compra"`. */
export function customFieldKeyFromLabel(text: string): string {
  const key = normalizeLabel(text)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return /^[a-z]/.test(key) ? key : `field_${key}`;
}

/* ------------------------------------------------------------------ values */

export type CustomFieldValueIssue = 'required' | 'invalid';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A real calendar date in `YYYY-MM-DD`. */
export function isIsoDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(value);
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Whether a value counts as "not filled" (for the required rule). */
export function isEmptyCustomFieldValue(value: CustomFieldValue | undefined): boolean {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0)
  );
}

/** What is wrong with `value` for `definition`; `null` when it is acceptable. */
export function customFieldValueIssue(
  definition: Pick<CustomFieldDefinition, 'type' | 'required' | 'options'>,
  value: CustomFieldValue | undefined,
): CustomFieldValueIssue | null {
  if (isEmptyCustomFieldValue(value)) return definition.required ? 'required' : null;
  const optionIds = new Set(definition.options.map((option) => option.id));
  switch (definition.type) {
    case 'text':
      return typeof value === 'string' && value.length <= CUSTOM_FIELD_LIMITS.textMax
        ? null
        : 'invalid';
    case 'long_text':
      return typeof value === 'string' && value.length <= CUSTOM_FIELD_LIMITS.longTextMax
        ? null
        : 'invalid';
    case 'number':
      return typeof value === 'number' && Number.isFinite(value) ? null : 'invalid';
    case 'money':
      return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? null : 'invalid';
    case 'date':
      return typeof value === 'string' && isIsoDate(value) ? null : 'invalid';
    case 'boolean':
      return typeof value === 'boolean' ? null : 'invalid';
    case 'single_select':
      return typeof value === 'string' && optionIds.has(value) ? null : 'invalid';
    case 'multi_select':
      return Array.isArray(value) &&
        new Set(value).size === value.length &&
        value.every((id) => optionIds.has(id))
        ? null
        : 'invalid';
    case 'email':
      return typeof value === 'string' && value.length <= 254 && EMAIL.test(value)
        ? null
        : 'invalid';
    case 'phone':
      return typeof value === 'string' && /^\d{10,11}$/.test(value) ? null : 'invalid';
    case 'url':
      return typeof value === 'string' && value.length <= 2048 && isHttpUrl(value)
        ? null
        : 'invalid';
  }
}

export interface CustomFieldValuesIssue {
  /** `customFields.<key>` */
  field: string;
  code: 'CUSTOM_FIELD_REQUIRED' | 'CUSTOM_FIELD_INVALID' | 'CUSTOM_FIELD_UNKNOWN';
  message: string;
}

/**
 * Validates the `customFields` of a write against the definitions of the entity (rules
 * 28–30). `create` checks every active required field; `update` only the keys sent (a
 * required field can't be emptied, and older records don't fail for fields added later).
 * Inactive fields are kept as sent, unchecked; unknown keys are refused. A deal field of
 * other pipelines is treated like an inactive one (QA-24).
 */
export function validateCustomFieldValues(
  definitions: readonly CustomFieldDefinition[],
  values: CustomFieldValues,
  mode: 'create' | 'update',
  pipelineId?: string | null,
): CustomFieldValuesIssue[] {
  const byKey = new Map(definitions.map((definition) => [definition.key, definition]));
  const issues: CustomFieldValuesIssue[] = [];
  for (const key of Object.keys(values)) {
    if (!byKey.has(key)) {
      issues.push({
        field: `customFields.${key}`,
        code: 'CUSTOM_FIELD_UNKNOWN',
        message: `Unknown custom field "${key}"`,
      });
    }
  }
  for (const definition of definitions) {
    if (!definition.active || !appliesToPipeline(definition, pipelineId)) continue;
    const sent = Object.hasOwn(values, definition.key);
    if (mode === 'update' && !sent) continue;
    const issue = customFieldValueIssue(definition, values[definition.key]);
    if (issue) {
      issues.push({
        field: `customFields.${definition.key}`,
        code: issue === 'required' ? 'CUSTOM_FIELD_REQUIRED' : 'CUSTOM_FIELD_INVALID',
        message:
          issue === 'required'
            ? `Custom field "${definition.key}" is required`
            : `Invalid value for custom field "${definition.key}"`,
      });
    }
  }
  return issues;
}

/** Whether a field shows on a deal of `pipelineId`: it names none, or names that one. */
export function appliesToPipeline(
  definition: Pick<CustomFieldDefinition, 'pipelineIds'>,
  pipelineId: string | null | undefined,
): boolean {
  const pipelines = definition.pipelineIds ?? [];
  return pipelines.length === 0 || !pipelineId || pipelines.includes(pipelineId);
}

/**
 * Active definitions in form order (by `order`). With `pipelineId` (deal forms), only the
 * fields of that pipeline (QA-24).
 */
export function activeDefinitions(
  definitions: readonly CustomFieldDefinition[] | undefined,
  pipelineId?: string | null,
): CustomFieldDefinition[] {
  return (definitions ?? [])
    .filter((definition) => definition.active && appliesToPipeline(definition, pipelineId))
    .toSorted((a, b) => a.order - b.order);
}

/** Definitions grouped by section, in order of their first field. `null` = default section. */
export function groupDefinitions(
  definitions: readonly CustomFieldDefinition[],
): { group: string | null; definitions: CustomFieldDefinition[] }[] {
  const groups = new Map<string | null, CustomFieldDefinition[]>();
  for (const definition of definitions.toSorted((a, b) => a.order - b.order)) {
    const list = groups.get(definition.group) ?? [];
    list.push(definition);
    groups.set(definition.group, list);
  }
  return [...groups.entries()].map(([name, list]) => ({ group: name, definitions: list }));
}
